import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import EnterpriseSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseSpecializationEnrollButtonNotConfigurable';
import HeroImage from 'bundles/unified-description-page-common/components/modules/hero/HeroImage';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModuleWithImage from 'bundles/unified-description-page-common/components/modules/hero/HeroModuleWithImage';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import HeroSpecializationDescription from 'bundles/unified-description-page-common/components/modules/hero/specialization/HeroSpecializationDescription';
import KeyInformationSpecializationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationSpecializationHorizontalModule';
import ProductOverviewDescription from 'bundles/unified-description-page-common/components/modules/product-overview/Description';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getCourseProductUrl } from 'bundles/unified-description-page-common/utils';
import { getEnterpriseSpecializationKeyInformationItems } from 'bundles/unified-description-page-common/utils/getEnterpriseKeyInformationItems';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForSpecialization from 'bundles/unified-description-page-common/utils/getPartnersForSpecialization';
import getProductDetailItems from 'bundles/unified-description-page-common/utils/getProductDetailItems';
import getSpecializationDescription from 'bundles/unified-description-page-common/utils/getSpecializationDescription';

type Props = {
  pageData: EnterpriseSpecializationPageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

const EnterpriseSpecializationProductOverviewModuleRenderer = ({ pageData, config }: Props) => {
  const { navigationLinks } = usePageNavigation();
  const { programSlug } = useParams();
  const productTitle = getProductTitle(pageData.name, pageData.productVariant, pageData.slug);
  const partnersToDisplay = getPartnersForSpecialization(pageData.partners, !!pageData.coBrandingEnabled);
  const features = useEnterpriseProductFeatures();
  const firstCourseHomeLink =
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    pageData.courses?.[0].slug && `${getCourseProductUrl(pageData.courses?.[0].slug, false)}/home`;
  const overviewSkills = <Skills skills={pageData.skills} />;
  const descriptionWithProjectOverview = getSpecializationDescription(
    pageData.cmlDescription,
    pageData.projectsOverview
  );
  const overviewDescription = pageData.cmlLearningObjectives ? (
    <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} />
  ) : (
    <ProductOverviewDescription
      description={pageData.description}
      productId={pageData.id}
      cmlDescription={descriptionWithProjectOverview}
    />
  );
  const details = <Details detailItems={getProductDetailItems(pageData, config?.productDetailItems || [])} />;

  const description = (
    <React.Fragment>
      <HeroSpecializationDescription headline={pageData.headline} subheader={pageData.subheader} />
      {config?.showHeroLanguageInfo && (
        <HeroLanguageInfo
          languages={{
            primaryLanguages: pageData.primaryLanguages,
            subtitleLanguages: pageData.subtitleLanguages ?? [],
            translatedLanguages: pageData.translatedLanguages ?? [],
            dubbedLanguages: pageData.dubbedLanguages ?? [],
          }}
        />
      )}
    </React.Fragment>
  );

  const { items: keyInformationItems } = getEnterpriseSpecializationKeyInformationItems({
    pageData,
    keyInformationModuleConfig: { ...config?.keyInfoModule },
    productFeatures: features,
  });

  return (
    <ProductOverviewHorizontalModule
      anchorId={SPECIALIZATION_NAV_ITEMS.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModuleWithImage
          showBackgroundImage={!!config?.showBackgroundImage}
          backgroundImage={config?.backgroundImage}
          productTitle={productTitle}
          partnerLogo={
            partnersToDisplay && partnersToDisplay.length > 1 ? (
              <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={false} />
            ) : (
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              <HeroPartnerLogo image={partnersToDisplay?.[0].primaryLogo} partnerName={partnersToDisplay?.[0].name} />
            )
          }
          description={description}
          instructors={
            <HeroInstructors
              slug={pageData.slug}
              instructors={pageData.instructors}
              partners={pageData.partners}
              disableCTA={config?.removeLinksAboveFold}
              displayTopInstructorBadgeInline
            />
          }
          primaryCta={
            <EnterpriseSpecializationEnrollButtonNotConfigurable
              productId={pageData.id}
              shouldShowSponsoredByMessage={features?.shouldShowSponsoredByMessage}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              firstCourseId={pageData.courses?.[0].id}
              firstCourseHomeLink={firstCourseHomeLink}
            />
          }
          secondaryCta={
            config?.showAssignButton && (
              <AssignButtonNotConfigurable
                productSlug={pageData.slug}
                productType={
                  pageData.productVariant === 'PROFESSIONAL_CERTIFICATE' ? 'professional-certificate' : 'specialization'
                }
                programSlug={programSlug}
              />
            )
          }
          additionalInfo={<HeroTotalEnrollmentCount count={pageData?.totalEnrollmentCount} />}
          secondaryInfo={!config?.showBackgroundImage && <HeroImage />}
        />
      }
      keyInformation={
        <KeyInformationSpecializationHorizontalModule
          keyInformationItems={keyInformationItems}
          applyLightShadow={!!config?.heroModule?.backgroundColor}
        />
      }
      aboutSubmodule={
        <AboutSubmoduleRenderer
          skills={overviewSkills}
          description={overviewDescription}
          details={details}
          config={config}
        />
      }
    />
  );
};

export default EnterpriseSpecializationProductOverviewModuleRenderer;
